import apiClient from './apiClient';
import { User } from '../types';

/**
 * Data structure for password change request
 */
export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Email preferences structure
 */
export interface EmailPreferences {
  all: boolean;
}

/**
 * Account information returned by the backend
 */
export interface AccountInfo {
  user: User;
  emailPreferences: EmailPreferences;
  subscription?: {
    status: string;
    endDate?: Date;
    customerId?: string;
  };
}

/**
 * Change user password
 */
export const changePassword = async (passwordData: ChangePasswordData): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.put('/auth/change-password', passwordData);

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Password changed successfully'
      };
    }

    throw new Error(response.data.message || 'Password change failed');
  } catch (error: any) {
    if (error.response) {
      const serverError = error.response.data;
      if (serverError.errors && Array.isArray(serverError.errors)) {
        const errorMessage = serverError.errors.map((err: any) => err.msg).join(', ');
        throw new Error(`Password change failed: ${errorMessage}`);
      }
      throw new Error(serverError.message || 'Password change failed');
    } else if (error.request) {
      throw new Error('Unable to connect to server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Password change failed');
    }
  }
};

/**
 * Get user email preferences
 */
export const getEmailPreferences = async (): Promise<EmailPreferences> => {
  try {
    const response = await apiClient.get('/user/email-preferences');

    if (response.data.success) {
      return response.data.preferences || { all: true };
    }

    throw new Error(response.data.message || 'Failed to fetch email preferences');
  } catch (error: any) {
    if (error.response) {
      const serverError = error.response.data;
      throw new Error(serverError.message || 'Failed to fetch email preferences');
    } else if (error.request) {
      throw new Error('Unable to connect to server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch email preferences');
    }
  }
};

/**
 * Update user email preferences
 */
export const updateEmailPreferences = async (preferences: EmailPreferences): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.put('/user/email-preferences', preferences);

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Email preferences updated successfully'
      };
    }

    throw new Error(response.data.message || 'Failed to update email preferences');
  } catch (error: any) {
    if (error.response) {
      const serverError = error.response.data;
      if (serverError.errors && Array.isArray(serverError.errors)) {
        const errorMessage = serverError.errors.map((err: any) => err.msg).join(', ');
        throw new Error(`Email preferences update failed: ${errorMessage}`);
      }
      throw new Error(serverError.message || 'Failed to update email preferences');
    } else if (error.request) {
      throw new Error('Unable to connect to server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to update email preferences');
    }
  }
};

/**
 * Get complete account information including user data, email preferences, and subscription status
 */
export const getAccountInfo = async (): Promise<AccountInfo> => {
  try {
    const response = await apiClient.get('/user/account-info');

    if (response.data.success) {
      return {
        user: response.data.user,
        emailPreferences: response.data.emailPreferences || { all: true },
        subscription: response.data.subscription
      };
    }

    throw new Error(response.data.message || 'Failed to fetch account information');
  } catch (error: any) {
    if (error.response) {
      const serverError = error.response.data;
      throw new Error(serverError.message || 'Failed to fetch account information');
    } else if (error.request) {
      throw new Error('Unable to connect to server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch account information');
    }
  }
};